import React, { useMemo, useState } from 'react';
import { useGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { History } from 'lucide-react';
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

type AuditRow = {
  id: string;
  quote_id: string;
  step: string | null;
  payload: unknown;
  created_at: string;
};

function formatTimestamp(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

function OnboardingHistoryDialog({
  quoteId,
  open,
  onClose,
}: {
  quoteId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetList<AuditRow>(
    'form_audit_log',
    {
      filter: { quote_id: quoteId },
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'created_at', order: 'ASC' },
    },
    { enabled: open && !!quoteId },
  );

  // Mark a row as a retry when the same step has already been submitted earlier.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    return (data || []).map((row) => {
      const key = row.step || 'unknown';
      const isRetry = seen.has(key);
      seen.add(key);
      return { ...row, isRetry };
    });
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Onboarding history
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          Quote {quoteId.slice(0, 8)} · {rows.length} event{rows.length === 1 ? '' : 's'}
        </span>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && rows.length === 0 && (
          <div className="text-sm text-muted-foreground">
            No onboarding events recorded for this funeral yet.
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="divide-y divide-border">
            {rows.map((row, idx) => (
              <details
                key={row.id}
                className="py-2 group"
                open={idx === 0}
              >
                <summary className="cursor-pointer list-none flex items-center gap-3 py-1">
                  <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatTimestamp(row.created_at)}
                  </span>
                  <span className="text-sm font-medium">{row.step || 'unknown'}</span>
                  {row.isRetry && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                      retry
                    </span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground group-open:hidden">▸</span>
                  <span className="ml-auto text-xs text-muted-foreground hidden group-open:inline">▾</span>
                </summary>
                <pre className="mt-2 ml-0 text-xs bg-muted/50 rounded p-3 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(row.payload, null, 2)}
                </pre>
              </details>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function OnboardingHistorySection({ funeralId }: { funeralId: number | string }) {
  const [open, setOpen] = useState(false);

  const { data: quotes } = useGetList(
    'quotes',
    {
      filter: { funeral_id: funeralId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: !!funeralId },
  );
  const quoteId = quotes?.[0]?.id as string | undefined;

  const { total } = useGetList(
    'form_audit_log',
    {
      filter: { quote_id: quoteId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: !!quoteId },
  );

  return (
    <AccordionItem value="onboarding">
      <AccordionTrigger icon={<History className="w-4 h-4" />}>
        Onboarding
      </AccordionTrigger>
      <AccordionContent>
        <Button
          fullWidth
          variant="outlined"
          disabled={!quoteId}
          onClick={() => setOpen(true)}
        >
          View history{quoteId ? ` (${total ?? 0})` : ''}
        </Button>
        {!quoteId && (
          <div className="mt-2 text-xs text-muted-foreground">
            No quote linked to this funeral.
          </div>
        )}
        {open && quoteId && (
          <OnboardingHistoryDialog
            quoteId={quoteId}
            open={open}
            onClose={() => setOpen(false)}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
